import cx from 'classnames';
import { useDispatch } from 'react-redux';
import { useCallback, useEffect, useRef } from 'react';

import { ParameterType } from '../../interfaces';
import { THEME } from '../../../../../config/commons';
import Icon from '../../../../../common/components/Icon/Icon';
import * as weighingTransfersSlice from '../../weighingTransfersSlice';
import { LrvText } from '../../../../../common/components/LrvText/LrvText';
import { TransferMetricEntry } from '../../../../Reports/TransferGraph/interfaces';
import { MiniTransferGraphD3 } from '../../../../Reports/TransferGraph/MiniTransferGraphD3';

import styles from './MiniTransferMetricChart.module.scss';

interface Props {
  title: string;
  transferMetrics: TransferMetricEntry[];
  parameter: ParameterType;
  width: number;
  theme?: 'dark' | 'light';
}

export const MiniTransferMetricChart = (props: Props) => {
  const { title, transferMetrics, parameter, theme, width } = props;
  const dispatch = useDispatch();

  const isLightTheme = theme === THEME.LIGHT;

  const refChartMain = useRef<HTMLDivElement>(null);
  const chart = useRef<MiniTransferGraphD3 | null>(null);

  const height = 120;

  useEffect(() => {
    return () => {
      chart.current = null;
    };
  }, []);

  const refreshChart = useCallback(() => {
    if (!transferMetrics.length) {
      return;
    }

    chart.current = new MiniTransferGraphD3({
      container: refChartMain.current,
      transferMetrics,
      height,
      width,
    });
  }, [transferMetrics, width, height, refChartMain.current]);

  useEffect(() => {
    refreshChart();
  }, [refreshChart]);

  return (
    <div
      className={cx(styles.container, isLightTheme ? styles.lightContainer : styles.darkContainer)}
      onClick={() => {
        dispatch(weighingTransfersSlice.setShowChartModal(true));
        dispatch(weighingTransfersSlice.setSelectedTransferMetricEntry(transferMetrics));
        dispatch(weighingTransfersSlice.setSelectedParameter(parameter));
      }}
    >
      <div className={styles.header}>
        <LrvText className={styles.title} text={title} theme={theme} />

        <div className={styles.iconContainer}>
          <Icon name='expand-diagonal' theme={theme} className={styles.icon} />
        </div>
      </div>

      <div
        ref={refChartMain}
        className={styles.chart}
      />
    </div>
  );
};
